/**
 * Icon Utilities
 * Provides icon rendering functions for travel modes and other icons
 * Priority: 1) Icon folder SVGs, 2) Lucide icons, 3) null
 * Follows Single Responsibility Principle
 */

import React from 'react';
import Image from 'next/image';
import { Plane, Train, Bus, Car, Ship, Bike, Navigation } from 'lucide-react';

// Map icon names to SVG paths in icons folder
const ICON_FOLDER_MAP: Record<string, string> = {
  plane: '/assets/images/icons/plane.svg',
  train: '/assets/images/icons/train.svg',
  bus: '/assets/images/icons/bus.svg',
  car: '/assets/images/icons/car.svg',
  trekking: '/assets/images/icons/trekking.svg',
};

// Map icon names to Lucide icon components (fallback)
const LUCIDE_ICON_MAP: Record<
  string,
  React.ComponentType<{ size?: number; color?: string }>
> = {
  plane: Plane,
  train: Train,
  bus: Bus,
  car: Car,
  ship: Ship,
  bicycle: Bike,
  bike: Bike,
  trekking: Navigation,
  walking: Navigation,
  walk: Navigation,
};

export const getTravelIcon = (iconName: string): React.ReactNode => {
  if (!iconName) return null;

  // Handle icon URLs from API (starts with http or /)
  if (
    typeof iconName === 'string' &&
    (iconName.startsWith('http') || iconName.startsWith('/'))
  ) {
    return (
      <div
        style={{
          width: '20px',
          height: '20px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Image
          src={iconName}
          alt='Travel mode icon'
          fill
          style={{ objectFit: 'contain' }}
          unoptimized={true}
        />
      </div>
    );
  }

  // Priority 1: Check if icon exists in icons folder
  const iconPath = ICON_FOLDER_MAP[iconName];
  if (iconPath) {
    return (
      <div
        style={{
          width: '20px',
          height: '20px',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Image
          src={iconPath}
          alt={`${iconName} icon`}
          fill
          style={{ objectFit: 'contain' }}
          unoptimized={true}
        />
      </div>
    );
  }

  // Priority 2: Use Lucide icon as fallback
  const LucideIcon = LUCIDE_ICON_MAP[iconName];
  if (LucideIcon) {
    return (
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <LucideIcon size={20} color='#4c4c4c' />
      </div>
    );
  }

  // No icon found
  return null;
};
